import {Fragment, useState} from 'react'

import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import {useForm} from 'react-hook-form'
import PropTypes from 'prop-types'

import FormWrapper from '../../shared/FormWrapper'
import Input from '../../shared/Input'
import Modal from '../../shared/Modal'

import {connect} from '../../../state/hooks/tools'
import {createResource} from '../../../lib/asyncMethods'
import {RECOVERY_INVITES} from '../../../state/actionTypes'

/**
 * What a recovery contact sees when they open their invitation.
 *
 * This person has no account here and in most cases never will, so the page asks for the two things
 * the API needs and nothing else. The password they choose protects their piece of the key and
 * gives them access to nothing, which is worth saying outright — otherwise it reads like an account
 * they now have to look after.
 *
 * Declining sits beside accepting rather than being hidden, but stays a text button: equally
 * available, plainly not the ask. It is also final, so it asks once before going through.
 */
const RecoveryInvitation = props => {
  const [mode, setMode] = useState('invitation')
  const [declining, setDeclining] = useState(false)
  const [error, setError] = useState('')
  const methods = useForm()
  const {errors} = methods.formState

  const toggleDeclining = () => {
    setDeclining(!declining)
  }

  const respond = ({path, attributes, onSuccess}) => {
    setError('')

    props.createResource({
      endpoint: `recovery-invites/${props.invite.id}/${path}`,
      attributes: {...attributes, token: props.token}
    }).then(response => {
      if (response.success)
        setMode(onSuccess)
      else
        setError(response.message)
    })
  }

  const accept = data => {
    respond({path: 'accept', attributes: data, onSuccess: 'accepted'})
  }

  const decline = () => {
    setDeclining(false)
    respond({path: 'decline', onSuccess: 'declined'})
  }

  if (mode === 'accepted') {
    return (
      <Paper elevation={3} component='div' className='recovery-invitation center-layout'>
        <Typography variant='h5' gutterBottom>You're all set</Typography>
        <Typography variant='body1'>
          You're now one of {props.inviterName}'s recovery contacts. If they ever lose their password,
          you'll be asked for the password you just chose. There's nothing else to do — you can close
          this page.
        </Typography>
      </Paper>
    )
  }

  if (mode === 'declined') {
    return (
      <Paper elevation={3} component='div' className='recovery-invitation center-layout'>
        <Typography variant='h5' gutterBottom>Thanks for letting us know</Typography>
        <Typography variant='body1'>
          We've told {props.inviterName} you can't be one of their recovery contacts. There's nothing
          else for you to do, and you can close this page.
        </Typography>
      </Paper>
    )
  }

  return (
    <Fragment>
      <Paper elevation={3} component='div' className='recovery-invitation center-layout'>
        <Typography variant='h5' gutterBottom>Help {props.inviterName} get back in</Typography>
        <Typography variant='body1' gutterBottom>
          Hi {props.invite.firstName} — {props.inviterName} has chosen you as a recovery contact. If
          they ever lose their password, you'll be one of three people it takes to let them back in.
          You won't see anything in their account, and you can't start a recovery on your own.
        </Typography>
        <Typography variant='body2' gutterBottom>
          Choose a password. You'll be asked for it only if {props.inviterName} ever needs to recover
          their account — it doesn't give you access to anything of theirs.
        </Typography>
        {error.length > 0 && <Typography variant='body1' color='error' gutterBottom>{error}</Typography>}
        <FormWrapper methods={methods} handleSubmit={accept}>
          <Input
            error={!!errors.password}
            label='Password'
            name='password'
            type='password'
            options={{required: 'Password is required'}}
            helperText={errors.password?.message}
            margin='dense'
          />
          <Input
            error={!!errors.confirmPassword}
            label='Confirm Password'
            name='confirmPassword'
            type='password'
            options={{
              required: 'Confirm Password is required',
              validate: value => (value === methods.getValues('password') || 'Passwords do not match')
            }}
            helperText={errors.confirmPassword?.message}
            margin='dense'
          />
          <ButtonGroup direction='row'>
            <Button
              disabled={props.loading}
              type='submit'
              variant='contained'
              sx={{color: '#F7F7F7'}}>
              Accept
            </Button>
            <Button disabled={props.loading} onClick={toggleDeclining}>I'd rather not</Button>
          </ButtonGroup>
        </FormWrapper>
      </Paper>
      <Modal
        maxWidth='sm'
        open={declining}
        handleClose={toggleDeclining}
        buttonText='Close'
        title={`Tell ${props.inviterName} you can't help?`}>
        <Typography gutterBottom>
          This closes the invitation for good. If you change your mind later, {props.inviterName} will
          have to invite you again from scratch.
        </Typography>
        <ButtonGroup direction='row'>
          <Button onClick={toggleDeclining}>Go back</Button>
          <Button color='error' loading={props.loading} onClick={decline}>Decline</Button>
        </ButtonGroup>
      </Modal>
    </Fragment>
  )
}

RecoveryInvitation.propTypes = {
  invite: PropTypes.object.isRequired,
  inviterName: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired
}

const mapStateToProps = ({recovery}) => ({loading: recovery.loading})

const actionCreators = ({recovery}) => ({createResource: createResource(recovery, RECOVERY_INVITES)})

export default connect(mapStateToProps, actionCreators)(RecoveryInvitation)
